// Client HTTP de l'application.
//
// Une seule subtilité, mais elle porte toute l'expérience de session : le
// jeton d'accès expire au bout de 15 minutes. Plutôt que de déconnecter le
// joueur au milieu d'une série de trois balles, un 401 déclenche un
// rafraîchissement puis rejoue la requête, une seule fois.
//
// Les rafraîchissements concurrents sont fusionnés : trois appels qui
// expirent en même temps ne doivent pas faire tourner le refresh token trois
// fois — la rotation invaliderait les deux derniers.

export class ApiError extends Error {
    constructor(public status: number, message: string) {
        super(message);
        this.name = 'ApiError';
    }
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
    refreshInFlight ??= (async () => {
        try {
            const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'same-origin' });
            return res.ok;
        } catch {
            return false;
        } finally {
            // Libéré au tick suivant : les appels arrivés pendant celui-ci
            // partagent le même résultat, ceux d'après en relanceront un.
            queueMicrotask(() => { refreshInFlight = null; });
        }
    })();
    return refreshInFlight;
}

interface RequestOptions {
    method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
    body?: unknown;
    /** Interne : empêche une boucle de rafraîchissement. */
    _retried?: boolean;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, _retried = false } = options;

    const res = await fetch(path.startsWith('/api') ? path : `/api${path}`, {
        method,
        credentials: 'same-origin',
        headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401 && !_retried && !path.includes('/auth/')) {
        if (await refreshSession()) {
            return api<T>(path, { ...options, _retried: true });
        }
    }

    if (res.status === 204) return undefined as T;

    const payload: unknown = await res.json().catch(() => null);

    if (!res.ok) {
        const message =
            payload && typeof payload === 'object' && 'error' in payload
                ? String((payload as { error: unknown }).error)
                : `Erreur ${res.status}`;
        throw new ApiError(res.status, message);
    }

    return payload as T;
}
